import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {ICCSplitInvoiceStatus, PermissionEnum} from '@secca/shared/models/enums';
import {PermissionService} from '@secca/core/services/permission.service';
import {ICCSplitInvoiceItem} from '@secca/shared/models/ICCInvoiceSplit/i-c-c-split-invoice-item';
import {ICCSplitInvoice} from '@secca/shared/models/ICCInvoiceSplit/i-c-c-split-invoice';
import {SupplierInvoice} from '@secca/shared/models/supplierInvoice';
import {SupplierInvoiceService} from '@secca/core/services/supplier-invoice.service';
import {CoordinationCaseService} from '@secca/core/services/coordination-case.service';
import {DropdownDictionary} from '@secca/shared/models/dropdownDictionary';
import {MasterListService} from '@secca/core/services/masterlist-service';
import {ServiceItemType} from '@secca/shared/models/service-item-type';
import {forkJoin} from 'rxjs';
import {CaseLockHelperService} from '@secca/core/services/case-lock-helper.service';
import {CaseStateService} from '@secca/core/state-services/case-state.service';
import {ServiceOrderService} from '@secca/core/services/service-order.service';
import {NgxCurrencyInputMode} from 'ngx-currency';
import {
  ICCSplitEntry, ICCSplitEntryEvents
} from '@secca/case/components/case-economy/case-economy-coordination-case-split/coordination-case-split/icc-split-entry';

@Component({
  selector: 'app-coordination-case-split',
  templateUrl: './coordination-case-split.component.html',
  styleUrls: ['./coordination-case-split.component.scss'],
  standalone: false,
})
export class CoordinationCaseSplitComponent implements OnInit, ICCSplitEntryEvents {
  @Output()
  closeCoordinationCaseSplit = new EventEmitter<string>();
  @Input()
  supplierInvoiceId: number;
  @Input()
  caseId: string;

  coordinationCaseSplit: ICCSplitInvoice;
  linkedCases: ICCSplitInvoiceItem[];
  linkedCaseOptions: DropdownDictionary[];
  supplierInvoice: SupplierInvoice;
  allServiceItems: ServiceItemType[];
  iccSplitEntries: ICCSplitEntry[] = [];
  lineTotal = 0;
  lineTotalDKK = 0;
  amountFormat = '1.2-2';

  currencyMaskOptions = {
    prefix: '',
    thousands: '.',
    decimal: ',',
    precision: 2,
    allowNegative: true,
    inputMode: NgxCurrencyInputMode.Natural,
  };

  constructor(private permissionService: PermissionService,
              private coordinationCaseService: CoordinationCaseService,
              private supplierInvoiceService: SupplierInvoiceService,
              private masterListService: MasterListService,
              protected caseLockHelperService: CaseLockHelperService,
              protected caseStateService: CaseStateService,
              private serviceOrderService: ServiceOrderService) {
  }

  ngOnInit(): void {
    const split = this.supplierInvoiceService.getCoordinationCaseSplit(this.supplierInvoiceId);
    const supplierInvoice = this.supplierInvoiceService.getSupplierInvoice(this.supplierInvoiceId);

    this.masterListService.getServiceItemList().subscribe(result => {
      this.allServiceItems = result;
      forkJoin([split, supplierInvoice]).subscribe(results => {
        this.coordinationCaseSplit = results[0];
        this.supplierInvoice = results[1];
        this.linkedCases = this.coordinationCaseSplit.iccSplitInvoiceItems;
        this.iccSplitEntries = this.linkedCases.map(invoiceItem =>
          new ICCSplitEntry(this.serviceOrderService,
            invoiceItem,
            this.allServiceItems,
            this.coordinationCaseSplit.exchangeRate,
            this));
        this.setTotals();
      });
    });
    this.coordinationCaseService.getLinkedCasesForCoordinationCase(this.caseId).subscribe(cases => {
      this.linkedCaseOptions = cases.map(linkedCase => new DropdownDictionary(linkedCase.caseId + '', linkedCase.caseNumber));
    });
  }

  close() {
    this.closeCoordinationCaseSplit.emit('close');
  }

  itemAdd() {
    const item = this.createLinkedCaseSplitItem();
    this.coordinationCaseSplit.iccSplitInvoiceItems.push(item);
    this.iccSplitEntries.push(
      new ICCSplitEntry(this.serviceOrderService, item, this.allServiceItems, this.coordinationCaseSplit.exchangeRate, this));
  }

  deleteLinkedItem(linkedCaseSplitItem: ICCSplitInvoiceItem) {
    if (this.isApproved() || this.isCreditNote()) {
      return;
    }
    this.coordinationCaseSplit.iccSplitInvoiceItems = this.coordinationCaseSplit.iccSplitInvoiceItems
      .filter(item => item !== linkedCaseSplitItem);
    this.linkedCases = this.coordinationCaseSplit.iccSplitInvoiceItems;
    this.iccSplitEntries = this.iccSplitEntries.filter(entry => entry.item !== linkedCaseSplitItem);
    this.setTotals();
  }

  itemAmountChanged() {
    this.setTotals();
  }

  isApproved(): boolean {
    return this.coordinationCaseSplit?.status === ICCSplitInvoiceStatus.APPROVED;
  }

  isCreditNote(): boolean {
    return this.coordinationCaseSplit?.crediting !== null;
  }

  hasAccess(): boolean {
    return this.permissionService.checkUserPermission(PermissionEnum.SUPPLIER_INVOICE_UPDATE);
  }

  isApprovePossible(): boolean {
    return this.coordinationCaseSplit?.status !== ICCSplitInvoiceStatus.APPROVED &&
                                                  this.coordinationCaseSplit?.isValid() &&
                                                  this.isTotalValid();
  }

  isTotalValid(): boolean {
    return Math.abs(this.lineTotal) >= Math.abs(this.supplierInvoice?.invoiceAmountLocal.amount);
  }
  isDKKTotalValid(): boolean {
    return Math.abs(this.lineTotalDKK) >= Math.abs(this.supplierInvoice?.invoiceAmountExchanged.amount);
  }

  saveSplit() {
    this.supplierInvoiceService.updateCoordinationCaseSplit(this.supplierInvoiceId, this.coordinationCaseSplit).subscribe(split => {
      this.close();
    });
  }

  approveSplit() {
    this.coordinationCaseSplit.status = ICCSplitInvoiceStatus.APPROVING;
    this.saveSplit();
  }

  private createLinkedCaseSplitItem(): ICCSplitInvoiceItem {
    const newItem = new ICCSplitInvoiceItem();
    newItem.splitMoney = {amount: 0, currency: 'DKK', precision: 2};
    newItem.splitOriginalInvoiceMoney = {amount: 0, currency: this.coordinationCaseSplit.supplierInvoiceCurrency, precision: 2};
    return newItem;
  }

  private setTotals(){
    this.lineTotal = this.coordinationCaseSplit.getTotal();
    this.lineTotalDKK = this.coordinationCaseSplit.getTotalDKK();
  }
}
